import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  callAI, getAIUsage,
  SYSTEM_TRADE_REVIEW, SYSTEM_DAILY_REVIEW, SYSTEM_SETUP_VALIDATOR,
  formatTradeForAI, formatDailyTradesForAI
} from '../aiService';

const DEFAULT_DAILY_LIMIT = 20;

function UsageBadge({ usage }) {
  if (!usage) return null;
  const pct = (usage.count / usage.limit) * 100;
  const lowRemaining = usage.remaining <= 3;
  return (
    <div className={`ai-usage-badge ${lowRemaining ? 'low' : ''}`}>
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <span style={{fontSize:11,letterSpacing:1.5,color:'var(--text-3)'}}>AI CREDITS TODAY</span>
        <span style={{fontFamily:'DM Mono,monospace',fontSize:13,color: lowRemaining ? 'var(--amber)' : 'var(--gold-2)'}}>
          {usage.remaining} / {usage.limit} remaining
        </span>
      </div>
      <div className="ai-usage-track">
        <div className="ai-usage-fill" style={{width: `${100 - pct}%`}}></div>
      </div>
      <div style={{fontSize:10,color:'var(--text-4)',fontFamily:'DM Mono,monospace'}}>
        Resets at 00:00 UTC
      </div>
    </div>
  );
}

function ReviewedTradeCard({ trade, onReview, loading, review, disabled }) {
  return (
    <div className="card mb-16" style={{padding:18}}>
      <div className="flex-between mb-12">
        <div>
          <div style={{fontFamily:'DM Mono,monospace',fontSize:13,color:'var(--text-2)'}}>
            {trade.trade_date} · {trade.direction} · {trade.session || 'No session'}
          </div>
          <div style={{fontSize:12,color:'var(--text-3)',marginTop:4}}>
            {trade.entry_price} → {trade.exit_price} ·
            <span className={trade.r_multiple > 0 ? 'td-pos' : 'td-neg'} style={{marginLeft:6}}>
              {trade.r_multiple !== null ? trade.r_multiple + 'R' : '—'}
            </span> ·
            <span className={`badge ${trade.outcome === 'Win' ? 'green' : trade.outcome === 'Loss' ? 'red' : 'grey'}`} style={{marginLeft:6,fontSize:10}}>
              {trade.outcome}
            </span>
          </div>
          {trade.setup_type && <div style={{fontSize:12,color:'var(--gold-3)',marginTop:4}}>{trade.setup_type}</div>}
        </div>
        {!review && (
          <button className="btn-sm gold" onClick={() => onReview(trade)} disabled={loading || disabled}>
            {loading ? 'Analyzing...' : disabled ? 'Limit Reached' : 'AI Review'}
          </button>
        )}
      </div>
      {review && (
        <div className="ai-response">
          <div className="ai-response-header">
            <span style={{color:'var(--gold-2)'}}>◈ AI Coach Review</span>
          </div>
          <div className="ai-response-body">{review}</div>
        </div>
      )}
    </div>
  );
}

function TradeReviewTab({ usage, setUsage }) {
  const { user } = useAuth();
  const [trades, setTrades] = useState([]);
  const [reviews, setReviews] = useState({});
  const [loadingId, setLoadingId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    supabase
      .from('trades')
      .select('*')
      .eq('user_id', user.id)
      .order('trade_date', { ascending: false })
      .limit(20)
      .then(({ data }) => setTrades(data || []));
  }, [user.id]);

  async function review(trade) {
    setError('');
    setLoadingId(trade.id);
    try {
      const result = await callAI(SYSTEM_TRADE_REVIEW, formatTradeForAI(trade));
      setReviews(prev => ({ ...prev, [trade.id]: result.text }));
      if (result.usage) setUsage(result.usage);
    } catch (e) {
      setError(e.message);
    }
    setLoadingId(null);
  }

  const noCreditsLeft = usage && usage.remaining <= 0;

  return (
    <div>
      {error && <div className="error-banner">{error}</div>}
      {noCreditsLeft && (
        <div className="error-banner">
          ⏱ You've used all {usage.limit} AI credits for today. Resets at 00:00 UTC.
        </div>
      )}
      {trades.length === 0 ? (
        <div className="card" style={{textAlign:'center', padding:48, color:'var(--text-4)'}}>
          <div style={{fontSize:32,marginBottom:8}}>📭</div>
          <p>Log some trades in the Journal first — then come back to get AI coaching on each one.</p>
        </div>
      ) : (
        <>
          <p style={{fontSize:13,color:'var(--text-3)',marginBottom:16}}>
            Click <strong style={{color:'var(--gold-2)'}}>AI Review</strong> next to any trade to get a coach's analysis. Each review costs 1 credit.
          </p>
          {trades.map(t => (
            <ReviewedTradeCard
              key={t.id}
              trade={t}
              onReview={review}
              loading={loadingId === t.id}
              review={reviews[t.id]}
              disabled={noCreditsLeft}
            />
          ))}
        </>
      )}
    </div>
  );
}

function DailyReviewTab({ usage, setUsage }) {
  const { user } = useAuth();
  const [trades, setTrades] = useState([]);
  const [days, setDays] = useState(7);
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchRecent = useCallback(async () => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    const { data } = await supabase
      .from('trades')
      .select('*')
      .eq('user_id', user.id)
      .gte('trade_date', cutoff.toISOString().slice(0, 10))
      .order('trade_date', { ascending: false });
    setTrades(data || []);
  }, [user.id, days]);

  useEffect(() => { fetchRecent(); }, [fetchRecent]);

  async function runReview() {
    if (trades.length === 0) { setError('No trades in the selected period.'); return; }
    setError(''); setResponse('');
    setLoading(true);
    try {
      const result = await callAI(SYSTEM_DAILY_REVIEW, formatDailyTradesForAI(trades));
      setResponse(result.text);
      if (result.usage) setUsage(result.usage);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }

  const noCreditsLeft = usage && usage.remaining <= 0;

  return (
    <div>
      <div className="card mb-16">
        <div className="flex-between" style={{flexWrap:'wrap',gap:12}}>
          <div>
            <div className="card-title" style={{marginBottom:6}}>PERIOD TO REVIEW</div>
            <div style={{fontSize:13,color:'var(--text-3)'}}>
              Last {days} days · <span className="text-gold">{trades.length} trades</span> found
            </div>
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <select value={days} onChange={e => setDays(parseInt(e.target.value))}>
              <option value="3">Last 3 days</option>
              <option value="7">Last 7 days</option>
              <option value="14">Last 14 days</option>
              <option value="30">Last 30 days</option>
            </select>
            <button className="btn-gold" onClick={runReview} disabled={loading || trades.length === 0 || noCreditsLeft}>
              {loading ? 'Analyzing...' : noCreditsLeft ? 'Limit Reached' : 'Run AI Review'}
            </button>
          </div>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {noCreditsLeft && (
        <div className="error-banner">
          ⏱ You've used all {usage.limit} AI credits for today. Resets at 00:00 UTC.
        </div>
      )}

      {response && (
        <div className="card">
          <div className="ai-response">
            <div className="ai-response-header">
              <span style={{color:'var(--gold-2)'}}>◈ Coach's Report — Last {days} Days</span>
            </div>
            <div className="ai-response-body">{response}</div>
          </div>
        </div>
      )}

      {!response && !loading && (
        <div className="card" style={{textAlign:'center',padding:32,color:'var(--text-4)'}}>
          <div style={{fontSize:32,marginBottom:8}}>📊</div>
          <p>Click <strong style={{color:'var(--gold-2)'}}>Run AI Review</strong> to get a coach's analysis of your recent trading performance.</p>
        </div>
      )}
    </div>
  );
}

function SetupValidatorTab({ usage, setUsage }) {
  const [bias, setBias] = useState('Bullish');
  const [liquidity, setLiquidity] = useState('');
  const [sweep, setSweep] = useState('');
  const [choch, setChoch] = useState('');
  const [trigger, setTrigger] = useState('');
  const [entry, setEntry] = useState('');
  const [stop, setStop] = useState('');
  const [target, setTarget] = useState('');
  const [news, setNews] = useState('No major news within 30 min');
  const [extra, setExtra] = useState('');

  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const rr = (() => {
    const e = parseFloat(entry), s = parseFloat(stop), t = parseFloat(target);
    if (!e || !s || !t) return null;
    const risk = Math.abs(e - s);
    if (risk === 0) return null;
    const reward = Math.abs(t - e);
    return (reward / risk).toFixed(2);
  })();

  async function validate() {
    if (!entry || !stop || !target) { setError('Entry, stop, and target are required.'); return; }
    setError(''); setResponse('');
    setLoading(true);
    try {
      const prompt = `Setup details:

4H Bias: ${bias}
Liquidity Level: ${liquidity || 'Not described'}
Sweep on close: ${sweep || 'Not confirmed'}
1H CHoCH: ${choch || 'Not described'}
15M Entry Trigger: ${trigger || 'Not described'}
Entry Price: $${entry}
Stop Loss: $${stop}
Target: $${target}
Calculated R:R = 1:${rr || '?'}
News window: ${news}
Additional context: ${extra || 'None'}

Should this trade be taken? Be honest — most setups should be skipped or improved.`;

      const result = await callAI(SYSTEM_SETUP_VALIDATOR, prompt);
      setResponse(result.text);
      if (result.usage) setUsage(result.usage);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }

  const noCreditsLeft = usage && usage.remaining <= 0;

  return (
    <div>
      <p style={{fontSize:13,color:'var(--text-3)',marginBottom:16}}>
        Describe a potential setup before you enter. The AI coach will rate it 1-10 and give you a clear TAKE / SKIP / WAIT recommendation.
      </p>

      <div className="card mb-16">
        <div className="card-title">SETUP DESCRIPTION</div>
        <div className="journal-form">
          <div className="field">
            <label>4H BIAS</label>
            <select value={bias} onChange={e => setBias(e.target.value)}>
              <option>Bullish</option>
              <option>Bearish</option>
              <option>Unclear / Ranging</option>
            </select>
          </div>
          <div className="field">
            <label>NEWS WINDOW</label>
            <select value={news} onChange={e => setNews(e.target.value)}>
              <option>No major news within 30 min</option>
              <option>Major news in next 30 min</option>
              <option>Major news in next 1-2 hours</option>
              <option>Just had major news (within 30 min)</option>
            </select>
          </div>
          <div className="field full">
            <label>LIQUIDITY LEVEL TARGETED</label>
            <input type="text" placeholder="e.g. Equal highs at 2342, Asia session low at 2310" value={liquidity} onChange={e => setLiquidity(e.target.value)} />
          </div>
          <div className="field full">
            <label>SWEEP CONFIRMATION</label>
            <input type="text" placeholder="e.g. Wick to 2308, closed back above 2312 on 15M" value={sweep} onChange={e => setSweep(e.target.value)} />
          </div>
          <div className="field full">
            <label>1H CHoCH</label>
            <input type="text" placeholder="e.g. 1H made higher high at 2320 after sweep" value={choch} onChange={e => setChoch(e.target.value)} />
          </div>
          <div className="field full">
            <label>15M ENTRY TRIGGER</label>
            <input type="text" placeholder="e.g. Bullish engulfing candle closed at 2318, FVG fill" value={trigger} onChange={e => setTrigger(e.target.value)} />
          </div>
          <div className="field">
            <label>ENTRY PRICE</label>
            <input type="number" step="0.01" placeholder="2318.50" value={entry} onChange={e => setEntry(e.target.value)} />
          </div>
          <div className="field">
            <label>STOP LOSS</label>
            <input type="number" step="0.01" placeholder="2306.00" value={stop} onChange={e => setStop(e.target.value)} />
          </div>
          <div className="field">
            <label>TARGET</label>
            <input type="number" step="0.01" placeholder="2342.00" value={target} onChange={e => setTarget(e.target.value)} />
          </div>
          <div className="field">
            <label>R:R AUTO-CALC</label>
            <input
              readOnly
              value={rr ? `1:${rr}` : '— (fill entry, stop, target)'}
              style={{color: rr && parseFloat(rr) >= 2 ? 'var(--green)' : 'var(--amber)', cursor:'default'}}
            />
          </div>
          <div className="field full">
            <label>ADDITIONAL CONTEXT (optional)</label>
            <textarea placeholder="DXY position, time of day, any concerns..." value={extra} onChange={e => setExtra(e.target.value)} />
          </div>
          <div style={{gridColumn:'1/-1'}}>
            <button className="btn-gold" onClick={validate} disabled={loading || noCreditsLeft}>
              {loading ? 'Analyzing...' : noCreditsLeft ? 'Daily Limit Reached' : 'Validate Setup with AI'}
            </button>
          </div>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {noCreditsLeft && (
        <div className="error-banner">
          ⏱ You've used all {usage.limit} AI credits for today. Resets at 00:00 UTC.
        </div>
      )}

      {response && (
        <div className="card">
          <div className="ai-response">
            <div className="ai-response-header">
              <span style={{color:'var(--gold-2)'}}>◈ AI Coach Verdict</span>
            </div>
            <div className="ai-response-body">{response}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AICoach() {
  const [tab, setTab] = useState('validator');
  const [usage, setUsage] = useState(null);

  // Fetch usage on mount and after each AI call (children update it via setUsage prop)
  useEffect(() => {
    getAIUsage(DEFAULT_DAILY_LIMIT).then(u => {
      if (u) setUsage(u);
    });
  }, []);

  return (
    <div>
      <div className="page-header">
        <div className="flex-between" style={{flexWrap:'wrap',gap:12}}>
          <div>
            <h1 className="page-title">AI <em>Trading Coach</em></h1>
            <p className="page-subtitle">Get instant coaching on your trades</p>
          </div>
          <UsageBadge usage={usage} />
        </div>
      </div>

      <div className="admin-tabs" style={{marginBottom:20}}>
        <button className={`admin-tab ${tab === 'validator' ? 'active' : ''}`} onClick={() => setTab('validator')}>
          ✓ Setup Validator
        </button>
        <button className={`admin-tab ${tab === 'trade' ? 'active' : ''}`} onClick={() => setTab('trade')}>
          ◧ Trade Review
        </button>
        <button className={`admin-tab ${tab === 'daily' ? 'active' : ''}`} onClick={() => setTab('daily')}>
          ◔ Performance Review
        </button>
      </div>

      {tab === 'validator' && <SetupValidatorTab usage={usage} setUsage={setUsage} />}
      {tab === 'trade' && <TradeReviewTab usage={usage} setUsage={setUsage} />}
      {tab === 'daily' && <DailyReviewTab usage={usage} setUsage={setUsage} />}
    </div>
  );
}
